import { Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSubscriptionTagsInfo } from '@/api/subscriptionTags';
import { useSubscriptionsInfo } from '@/api/subscriptions';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { TelemetryUpdateConfirmationDialog } from '@/components/subscriptions/DeviceTelemetryTransactionManagement';
import { TelemetryTransactionSummary } from '@/components/subscriptions/TelemetryTransactionSummary';
import { TransactionIntentFooter } from '@/components/transactions/TransactionIntentFooter';
import { TransactionLineChangeSummary } from '@/components/transactions/TransactionLineChangeSummary';
import { TransactionModal, TransactionSummary } from '@/components/transactions/TransactionModal';
import { TransactionContextProvider, useTransactionContext } from '@/context/transaction';
import {
    TelemetryLineChangeContextProvider,
    useTelemetryLineChanges,
} from '@/context/transaction/TelemetryLineChangeContext';

export const CancelSubscriptionPage = () => (
    <TransactionContextProvider
        type="subscription"
        prorationBehavior="always_invoice"
        isSubscriptionCancellation
        defaultBackTarget="payment"
        totalDueLabel="Total credit to refund"
    >
        <TelemetryLineChangeContextProvider>
            <CancelSubscriptionModal />
        </TelemetryLineChangeContextProvider>
    </TransactionContextProvider>
);

const CancelSubscriptionModal = () => {
    const {
        updateSubscription,
        backTarget,
        mutationStatus: { isSuccess, isIdle } = {},
    } = useTransactionContext('subscription');
    const { disableAllLines, setRetainExtraLines } = useTelemetryLineChanges();
    const { data: subscriptionTagsInfo } = useSubscriptionTagsInfo({ config: { enabled: isIdle } });
    const { data: subscriptionsInfo } = useSubscriptionsInfo({ config: { enabled: isIdle } });
    const navigate = useNavigate();
    const [isPending, setPending] = useState(true);

    function handleDone() {
        navigate(backTarget?.href ?? '/payment');
    }

    useEffect(() => {
        if (!subscriptionTagsInfo?.data) {
            setPending(true);
            return;
        }

        setRetainExtraLines(false);
        disableAllLines();
        setPending(false);
    }, [disableAllLines, setRetainExtraLines, subscriptionTagsInfo?.data]);

    useEffect(() => {
        // if we got here but don't have a sub, just redirect back, but with exception
        // for if we just completed a transaction (in which case probably the success dialog is still open)
        if (subscriptionsInfo && subscriptionsInfo?.data.length === 0 && !isSuccess) {
            navigate(backTarget?.href ?? '/payment');
        }
    }, [backTarget?.href, isSuccess, navigate, subscriptionsInfo]);

    return (
        <TransactionModal>
            {isPending ? (
                <TransactionSummary>
                    <GenericSkeleton lines={4} />
                </TransactionSummary>
            ) : (
                <TelemetryTransactionSummary />
            )}
            <TransactionIntentFooter onConfirm={updateSubscription}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', flexGrow: 1 }}>
                    <TransactionLineChangeSummary hideUnusedLines />
                </Stack>
            </TransactionIntentFooter>
            <TelemetryUpdateConfirmationDialog onDone={handleDone} />
        </TransactionModal>
    );
};
